import { BaseController } from './core/base';
import { EdgeFunctions } from 'common/types/api';
import { DeepReadonly } from '@zajno/common/types';
import { LazyPromiseObservable } from '@zajno/common-mobx/lazy/observable';
import { makeObservable, observable } from 'mobx';
import { CampaignData, CampaignMainInfoUpdateData, CampaignStatus } from 'common/types/models/campaign';
import { CampaignsRepository } from 'common/repositories/campaigns';
import { PromiseCacheObservable } from '@zajno/common-mobx/structures/promiseCache';
import { CampaignAsset } from 'common/types/dtos/updateCampaignAsset';

type CheckoutInput = Omit<EdgeFunctions.Input<EdgeFunctions.Types.Checkout>, 'productSlug'>;

export class CampaignsController extends BaseController {

    private _items: LazyPromiseObservable<CampaignData[]>;
    private _repo: CampaignsRepository;

    constructor() {
        super();

        makeObservable<CampaignsController, '_items'>(this, {
            _items: observable.ref,
        });
    }

    /** Default: items available for current user */
    public get items(): ReadonlyArray<DeepReadonly<CampaignData>> { return this._items?.value; }

    public get repo() { return this._repo; }

    protected onInitialize(): void | Promise<void> {
        this._repo = new CampaignsRepositoryObservable(this.supabase)
            .useCaching(true);

        this._items = new LazyPromiseObservable(async () => {
            return this._repo.getCampaigns({ relations: { product: true, user: true } });
        });
    }

    public initializeCheckout(productSlug: string, input: CheckoutInput) {
        return this.runAction(async () => {
            const args: EdgeFunctions.Input<EdgeFunctions.Types.Checkout> = {
                ...input,
                productSlug,
            };

            // TODO validate?

            const result = await this.invokeFunction(EdgeFunctions.Types.Checkout, args);
            this.logger.log('checkout result =', result);

            return result.purchase;
        }, 'checkout');
    }

    public setStatus(campaignId: string, status: CampaignStatus) {
        return this.runAction(async () => {
            const args: EdgeFunctions.Input<EdgeFunctions.Types.UpdateStatus> = {
                campaignId,
                status,
            };

            const result = await this.invokeFunction(EdgeFunctions.Types.UpdateStatus, args);

            if (!result) {
                throw new Error('Failed to update campaign status');
            }

            this.logger.log('change status result =', result);

            // this will invalidate the observable, so it will be reloaded on next access
            this._items.reset();
            this._repo.updateCampaign(result.campaign);
        }, `setStatus, id = ${campaignId}, status = ${status}`);
    }

    public getCampaignsByUser(
        userId: string,
        offset?: number,
        limit?: number,
        searchString?: string,
    ) {
        return this.runAction(async () => {
            const config = {
                userId,
                relations: { product: true },
                orderNumber: searchString,
            };

            if (offset != null && limit != null) {
                const pages = { pages: { offset, limit } };
                Object.assign(config, { ...pages });
            }
            const data = await this._repo.getCampaigns(config);

            if (!data) {
                throw new Error(`Failed to get campaigns by user: ${userId}`);
            }

            return data;
        }, `getCampaignsByUser, userId = ${userId}`);
    }

    public getCampaigns(
        offset: number,
        limit: number,
        searchString?: string,
        filterByStatuses?: CampaignStatus[],
        ascending?: boolean,
    ) {
        return this.runAction(async () => {
            const data = await this._repo.getCampaigns({
                relations: { product: true, user: true, purchase: true },
                pages: { offset, limit },
                orderNumber: searchString,
                filterByStatuses: filterByStatuses,
                ascending,
            });

            if (!data) {
                throw new Error(`Failed to get campaigns: offset: ${offset}, limit: ${limit}`);
            }

            return data;
        }, 'getCampaigns with pagination');
    }

    public async addPlaylist(campaignId: string, playlist: string) {
        const campaign = await this._repo.getCampaign(campaignId);

        let newPlaylists: string[];

        if (campaign.playlists) {
            newPlaylists = [...campaign.playlists.links, playlist];
        } else {
            newPlaylists = [playlist];
        }

        return this.runAction(async () => {
            const args: EdgeFunctions.Input<EdgeFunctions.Types.UpdatePlaylists> = {
                campaignId,
                playlists: newPlaylists,
            };

            const result = await this.invokeFunction(EdgeFunctions.Types.UpdatePlaylists, args);

            if (!result) {
                throw new Error('Failed to add playlist to campaign');
            }

            this.logger.log('add playlist result =', result);

            // this will invalidate the observable, so it will be reloaded on next access
            this._items.reset();
            this._repo.updateCampaign(result.campaign);
        }, `add playlist to campaign, id = ${campaignId}, playlists = ${newPlaylists}`);
    }

    public async deletePlaylist(campaignId: string, playlist: string) {
        const campaign = await this._repo.getCampaign(campaignId);

        const newPlaylists = campaign.playlists.links.filter(p => p !== playlist);

        return this.runAction(async () => {
            const args: EdgeFunctions.Input<EdgeFunctions.Types.UpdatePlaylists> = {
                campaignId,
                playlists: newPlaylists,
            };

            const result = await this.invokeFunction(EdgeFunctions.Types.UpdatePlaylists, args);

            if (!result) {
                throw new Error('Failed to delete campaign playlist');
            }

            this.logger.log('delete playlist result =', result);

            // this will invalidate the observable, so it will be reloaded on next access
            this._items.reset();
            this._repo.updateCampaign(result.campaign);
        }, `delete playlist to campaign, id = ${campaignId}, playlists = ${newPlaylists}`);
    }

    public async updateAsset(campaignId: string, assetData: CampaignAsset) {
        return this.runAction(async () => {
            const args: EdgeFunctions.Input<EdgeFunctions.Types.UpdateCampaignAsset> = {
                campaignId,
                authorName: assetData.authorName,
                assetName: assetData.assetName,
                assetLink: assetData.assetLink,
                releaseDate: assetData.releaseDate,
            };

            const result = await this.invokeFunction(EdgeFunctions.Types.UpdateCampaignAsset, args);

            if (!result) {
                throw new Error('Failed to update campaign asset');
            }

            this.logger.log('change asset result =', result);

            // this will invalidate the observable, so it will be reloaded on next access
            this._items.reset();
            this._repo.updateCampaign(result.campaign);
        }, `updateAsset, id = ${campaignId}, asset = ${assetData}`);
    }

    public async updateCampaignMainInfo(campaignId: string, mainInfo: CampaignMainInfoUpdateData) {
        const { name, email, guaranteedStream } = mainInfo;

        return this.runAction(async () => {
            const args: EdgeFunctions.Input<EdgeFunctions.Types.UpdateCampaignMainInfo> = {
                campaignId,
                name,
                email,
                guaranteedStream,
            };

            const result = await this.invokeFunction(EdgeFunctions.Types.UpdateCampaignMainInfo, args);

            if (!result) {
                throw new Error('Failed to update campaign main info');
            }

            this.logger.log('update campaign main info result =', result);

            // this will invalidate the observable, so it will be reloaded on next access
            this._items.reset();
            this._repo.updateCampaign(result.campaign);
        }, `update campaign main info, id = ${campaignId}, mainInfo = ${mainInfo}`);
    }
}


class CampaignsRepositoryObservable extends CampaignsRepository {
    get PromiseCacheClass() { return PromiseCacheObservable; }
}
